<template>
  <div v-if="customizations">
    <div v-if="loadingCustomerAddresses" class="overlay">
      <Spinner message="Loading all customer addresses..." />
    </div>
    <Alert v-if="showTaxWarning" type="error" class="mb-2">
      <span>
        The address for this quote is missing a tax zone and no sales tax will
        be charged.
        <a href="#" @click.prevent="toggleModal('addressTaxSettingsModal')">
          Click here
        </a>
        to add one.
      </span>
    </Alert>
    <div
      class="d-flex flex-col items-end md:items-center md:flex-row justify-content-between"
    >
      <div class="block">
        <div class="flex items-center flex-wrap">
          <slot name="title">
            <h5 class="mb-0 whitespace-nowrap" data-cy="title">
              {{ title }}
            </h5>
          </slot>
          <div v-if="quote.sent_at" class="d-flex align-items-center">
            <div class="divider"></div>
            <Badge class="ml-0" v-bind="badgeParams" />
          </div>
        </div>
      </div>
      <div class="flex actions-wrapper">
        <div class="actions">
          <QuoteActionsContextMenu
            v-if="!editing"
            :quote="quote"
            :tippy-options="tippyOptions"
            @edit-quote="toggleEdit"
          >
            <template #trigger>
              <sera-button
                label="Quote Actions"
                icon="chevron-down"
                iconRight
              />
            </template>
          </QuoteActionsContextMenu>
          <sera-button
            v-else
            class="mr-1"
            label="Done"
            icon="check"
            iconRight
            @click="toggleEdit"
          />
        </div>
      </div>
    </div>
    <Quote
      :key="quoteKey"
      data-cy="quote"
      :quote="quote"
      :editing="editing"
      :late-fees-enabled="lateFeesEnabled"
      :customizations="customizations"
      :grouping-enabled="featureEnabledForTenant('item_grouping')"
      :edit-taxes-enabled="!missingTaxZone"
      @add-item="toggleModal('taskModal')"
      @replace-line-item="toggleTaskPicker"
      @discount-click="toggleModal('discountModal')"
      @taxes-click="toggleModal('taxSettingsModal')"
      @signature-click="toggleModal('approvalModal')"
      @late-fee-click="toggleModal('lateFeeModal')"
      @savings-click="handleSavingsClick"
      @swap-click="handleSwap"
      @remove-item="handleRemoveLineItem"
      @edit-quantity="handleEditQuantity"
      @edit-taxable="handleEditTaxable"
      @edit-unit-price="handleEditPrice"
      @membership-click="handleMembershipClick"
      @remove-discount="handleRemoveDiscount"
      @remove-late-fee="handleRemoveLateFee"
      @edit-late-fee="handleEditLateFee"
      @edit-unit-name="handleEditName"
      @edit-description="handleEditDescription"
      @edit-covered-systems="handleEditCoveredSystems"
      @line-items-sort="handleLineItemsSort"
      @add-address-click="toggleModal('addressModal')"
      @edit-quote-address="handleEditAddress"
      @edit-quote-name="handleEditQuoteName"
      @edit-quote-summary="handleEditQuoteSummary"
      @ungroup-item="ungroupItem"
      @action="handleAction"
    >
      <template #serviceAddressField="addressfieldProps">
        <div class="mb-2">
          <CustomerAddressSelect
            :customer="currentUser"
            :value="addressfieldProps.addressId"
            label="Service Address"
            @update:modelValue="
              ($event) => addressfieldProps.updateAddressId($event)
            "
          />
        </div>
      </template>
      <template #financingOptions>
        <FinancingAvailable
          v-if="quote && !editing && financingAvailable"
          data-cy="financingAvailable"
          :quote="quote"
          :payments-as-low-as-enabled="paymentsAsLowAsEnabled"
          :financing-offers-enabled="financingEnabled"
          :editable="financingEditable"
        />
      </template>
      <template #actionsIcon>
        <i class="fas fa-ellipsis-vertical" />
      </template>
      <template #swapIcon>
        <SwapIcon height="20" width="20" />
      </template>
      <template #spinner>
        <Spinner />
      </template>
      <template #deleteIcon>
        <i class="fas fa-trash-alt link" />
      </template>
    </Quote>
    <div
      v-if="financingEnabled && editingJoblessQuote"
      class="financing-option"
    >
      <Checkbox v-model="joblessFinancing" class="mt-2" />
      <span class="label ml-2">Include Financing Options</span>
    </div>
    <TaskPickerWrapper
      v-if="taskModal"
      :quote="quote"
      :quote-name="title"
      :replacing-task="replacingTask"
      @hide-modal="toggleTaskPicker"
    />
    <MembershipModal
      v-if="membershipModal"
      :quote="quote"
      :type="membershipType"
      :read-only="readOnly"
      @hide-modal="handleMembershipModalHide"
    />
    <DiscountModal
      v-if="discountModal"
      :quote="quote"
      @hide-modal="toggleModal('discountModal')"
    />
    <QuoteApprovalModal
      v-if="approvalModal"
      :quote="quote"
      @hide-modal="toggleModal('approvalModal')"
    />
    <LateFeeModal
      v-if="lateFeeModal"
      :quote="quote"
      @hide-modal="toggleModal('lateFeeModal')"
    />
    <AddressModal
      v-if="addressModal"
      :customer="currentUser"
      @hide-modal="toggleModal('addressModal')"
      @success-address-created="handleSuccessAddress"
    />
    <TaxSettingsModal
      :open="taxSettingsModal"
      :gid="quote.gid"
      context-type="Quote"
      :context="quote"
      :settings="quote.tax_setting"
      @close="taxSettingsModal = false"
      @refresh="refreshCurrentQuote"
    >
      <template #header>
        <div class="sera-body-3">
          <strong class="sera-bold">Quote:</strong>
          <span> #{{ quote.id }} </span>
        </div>
      </template>
    </TaxSettingsModal>
    <TaxSettingsModal
      :open="addressTaxSettingsModal"
      :gid="serviceAddress.gid"
      context-type="Address"
      :context="serviceAddress"
      :settings="serviceAddress.tax_setting"
      @close="addressTaxSettingsModal = false"
      @refresh="refreshCurrentQuote"
    >
      <template #header>
        <div class="sera-body-3">
          <strong class="sera-bold">Address:</strong>
          <span>{{ fullAddress(serviceAddress) }}</span>
        </div>
      </template>
    </TaxSettingsModal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { sentBadgeParams } from "@/lib/badgeHelpers.js";
import { EventBus } from "@/lib/event_bus";

import Badge from "@/components/Badge.vue";
import Button from "@/components/Button.vue";
import Checkbox from "@/components/Checkbox.vue";
import MembershipModal from "@/components/MembershipModal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Spinner.vue";
import AddressModal from "@/Customer/AddressModal/AddressModal.vue";
import CustomerAddressSelect from "@/Customer/AddressSelector/CustomerAddressSelect.vue";
import QuoteActionsContextMenu from "@/Customer/Profile/QuoteActionsContextMenu.vue";
import FinancingAvailable from "@/Financing/FinancingAvailable/FinancingAvailable.vue";
import SwapIcon from "@/images/swap_icon.svg?component";
import TaxSettingsModal from "@/Taxability/TaxSettingsModal.vue";
import { hasMissingTaxZone } from "@/typings/taxability";
import DiscountModal from "./DiscountModal.vue";
import LateFeeModal from "./LateFeeModal.vue";
import QuoteApprovalModal from "./QuoteApprovalModal.vue";
import TaskPickerWrapper from "./TaskPickerWrapper.vue";

const findItem = (items, itemToFind) => {
  let itemIndex = -1;
  let groupIndex = -1;
  items.forEach((item, index) => {
    if (item.id === itemToFind.id) {
      itemIndex = index;
      return;
    }
    if (!item.items_in_group) return;
    item.items_in_group.forEach((groupItem, groupItemIndex) => {
      if (groupItem.id === itemToFind.id) {
        itemIndex = index;
        groupIndex = groupItemIndex;
      }
      return;
    });
  });
  return {
    itemIndex,
    groupIndex
  };
};

export default {
  components: {
    CustomerAddressSelect,
    FinancingAvailable,
    Spinner,
    MembershipModal,
    DiscountModal,
    QuoteApprovalModal,
    LateFeeModal,
    TaskPickerWrapper,
    TaxSettingsModal,
    Button,
    AddressModal,
    QuoteActionsContextMenu,
    SwapIcon,
    Checkbox,
    Badge,
    Alert
  },
  props: {
    quote: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      editing: this.quote?.editing || false,
      membershipType: "",
      taskModal: false,
      membershipModal: false,
      readOnly: false,
      discountModal: false,
      approvalModal: false,
      lateFeeModal: false,
      addressModal: false,
      taxSettingsModal: false,
      addressTaxSettingsModal: false,
      emailing: false,
      replacingTask: null,
      joblessFinancing: this.quote?.jobless_financing || false,
      loadingCustomerAddresses: false,
      quoteKey: 1
    };
  },
  computed: {
    ...mapGetters([
      "configSupportedForTenant",
      "financingEnabled",
      "paymentsAsLowAsEnabled",
      "featureEnabledForTenant"
    ]),
    ...mapState(["currentUser"]),
    ...mapState({
      lateFeesEnabled: function () {
        return this.quote && this.quote.settings
          ? this.quote.settings.late_fee_per_minute_cents > 0
          : false;
      },
      customizations: ({ tenantCustomizations, tenant }) => {
        if (!tenant || !tenantCustomizations) {
          return null;
        }
        return {
          ...tenantCustomizations,
          Tenant: { ...tenant }
        };
      }
    }),
    title() {
      return `Quote #${this.quote?.id}`;
    },
    isDeclined() {
      return this.quote?.status === "denied";
    },
    isDraft() {
      return this.quote?.status === "draft";
    },
    isPending() {
      return this.quote?.status === "pending";
    },
    isEditable() {
      return this.quote?.links?.quote_update !== undefined;
    },
    isJoblessQuote() {
      return this.quote?.job_id === null;
    },
    isFinanceableQuote() {
      return !this.isJoblessQuote || this.joblessFinancing;
    },
    financingEditable() {
      return this.isDraft && this.isEditable;
    },
    financingAvailable() {
      if (!this.financingEnabled) {
        return false;
      }
      if (this.quote.financing_options_active) {
        return true;
      }
      if (this.paymentsAsLowAsEnabled && this.isFinanceableQuote) {
        return true;
      }

      return false;
    },
    editingJoblessQuote() {
      return this.editing && this.isJoblessQuote;
    },
    declinedButtonType() {
      return this.isDeclined ? "primary" : "secondary";
    },
    declinedButtonText() {
      return this.isDeclined ? "Reactivate Quote" : "Decline Quote";
    },
    tippyOptions() {
      return {
        distance: "10",
        placement: "bottom",
        trigger: "click",
        hideOnClick: true
      };
    },
    serviceAddress() {
      return this.quote?.service_address;
    },
    missingTaxZone() {
      return (
        this.serviceAddress &&
        hasMissingTaxZone(this.serviceAddress.tax_setting)
      );
    },
    showTaxWarning() {
      return this.missingTaxZone && (this.isDraft || this.isPending);
    },
    badgeParams() {
      return sentBadgeParams(this.quote.sent_at);
    }
  },
  watch: {
    joblessFinancing(newValue) {
      this.updateQuote({ jobless_financing: newValue });
    }
  },
  mounted() {
    EventBus.$on("refresh-quote", this.refreshCurrentQuote);
  },
  beforeUnmount() {
    EventBus.$off("refresh-quote", this.refreshCurrentQuote);
  },
  methods: {
    ...mapActions([
      "addItem",
      "confirmChanges",
      "confirmDelete",
      "removeItem",
      "updateItem",
      "updateItemNoConfirm",
      "swapMembershipProgram",
      "updateDiscount",
      "updateLateFee",
      "confirmQuoteChange",
      "regroupItems",
      "setCurrentQuote",
      "refreshCurrentQuote",
      "updateQuote",
      "updateTaxSettings"
    ]),
    handleItemUpdate(updateItem, item) {
      updateItem(item);
    },
    handleEditQuantity(e, item) {
      this.handleItemUpdate(this.updateItem, {
        ...item,
        quantity: e.target.value
      });
    },
    async handleEditTaxable(e, item) {
      const taxability = e.target.value ? "taxable" : "nontaxable";
      try {
        await this.updateTaxSettings({
          id: item.tax_setting?.id,
          configurable_gid: item.gid,
          taxability,
          reason: "Line Item Toggle"
        });
        this.refreshCurrentQuote();
      } catch (e) {
        this.quoteKey += 1;
      }
    },
    handleEditPrice(e, item) {
      this.handleItemUpdate(this.updateItem, {
        ...item,
        unit_price_cents: e.target.value.toFixed(2)
      });
    },
    handleEditName(e, item) {
      this.handleItemUpdate(this.updateItemNoConfirm, {
        ...item,
        unit_name: e.target.value
      });
    },
    handleEditQuoteName(e) {
      this.updateQuote({
        name: e.target.value
      });
    },
    handleEditQuoteSummary(e) {
      this.updateQuote({
        summary: e.target.value
      });
    },
    handleMembershipClick(id, type) {
      this.membershipType = type;
      this.readOnly = false;
      this.toggleModal("membershipModal");
    },
    handleMembershipModalHide() {
      this.membershipType = "";
      this.readOnly = false;
      this.toggleModal("membershipModal");
    },
    handleSavingsClick() {
      this.membershipType = "actual";
      this.readOnly = true;
      this.toggleModal("membershipModal");
    },
    handleSwap(callback) {
      this.confirmChanges({
        title: "Swap Membership Program",
        message:
          "Are you sure you want to swap the membership program selections? This will reset any price overrides on the quote."
      })
        .then(() => {
          this.swapMembershipProgram(this.quote).then(() => callback());
        })
        .catch(() => {
          callback();
        });
    },
    async handleRemoveLineItem(item) {
      await this.confirmDelete({
        message: "Are you sure you want to remove this item?",
        title: "Remove Line Item"
      });
      this.removeItem(item.links.line_item_delete);
    },
    handleRemoveDiscount() {
      this.updateDiscount({
        quote_update_url: this.quote.links.quote_update,
        discountId: null
      });
    },
    handleRemoveLateFee() {
      this.handleEditLateFee({ target: { value: 0 } });
    },
    handleEditLateFee(e) {
      this.updateLateFee({
        quote: this.quote,
        lateFee: e.target.value
      });
    },
    toggleModal(modal) {
      this[modal] = !this[modal];
    },
    toggleEdit() {
      this.editing = !this.editing;
    },
    handleEditDescription(e, item) {
      this.handleItemUpdate(this.updateItemNoConfirm, {
        ...item,
        description: e.target.value
      });
    },
    handleEditCoveredSystems(e, item) {
      this.handleItemUpdate(this.updateItem, {
        ...item,
        covered_systems: e.target.value
      });
    },
    handleLineItemsSort(items) {
      this.regroupItems({
        items,
        url:
          this.quote.links.line_item_regroup ||
          this.quote.links.line_item_reorder
      });
    },
    handleSuccessAddress(address) {
      this.setCurrentQuote({
        ...this.quote,
        service_address: address,
        service_address_id: address.id
      });
    },
    handleEditAddress(event) {
      this.updateQuote({
        service_address_id: event.target.value
      });
    },
    toggleTaskPicker(item = null) {
      this.replacingTask = item;
      this.toggleModal("taskModal");
    },

    ungroupItem(itemToUngroup) {
      const quoteItems = this.quote.items_attributes.slice(0);
      const { itemIndex, groupIndex } = findItem(quoteItems, itemToUngroup);
      quoteItems[itemIndex].items_in_group.splice(groupIndex, 1);
      itemToUngroup.group_id = null;

      if (quoteItems[itemIndex].items_in_group.length === 1) {
        quoteItems[itemIndex].group_id = null;
      }
      quoteItems.push(itemToUngroup);
      this.regroupItems({
        items: quoteItems,
        url: this.quote.links.line_item_regroup
      });
    },
    async handleAction(actionAndItem) {
      const quoteItems = this.quote.items_attributes.slice(0);
      if (actionAndItem.eventName === "ungroup-items") {
        const { itemIndex } = findItem(quoteItems, actionAndItem.item);
        quoteItems[itemIndex].group_id = null;
        quoteItems[itemIndex].items_in_group.forEach(
          (item) => (item.group_id = null)
        );
        this.regroupItems({
          items: quoteItems,
          url: this.quote.links.line_item_regroup
        });
      } else if (actionAndItem.eventName === "delete-main-item") {
        await this.confirmDelete({
          message:
            "Are you sure you want to remove this item? Removing this item will delete and ungroup the remaining items.",
          title: "Remove Main Item"
        });
        const { itemIndex } = findItem(quoteItems, actionAndItem.item);
        quoteItems[itemIndex].group_id = null;
        quoteItems[itemIndex].items_in_group.forEach(
          (item) => (item.group_id = null)
        );
        await this.regroupItems({
          items: quoteItems,
          url: this.quote.links.line_item_regroup
        });
        this.removeItem(actionAndItem.item.links.line_item_delete);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
a {
  color: var(--lightblue) !important;
}
.actions {
  padding: 10px 0;
}

.quote-action-button {
  position: absolute;
  bottom: 6%;
  left: 25%;
}

:deep(.table td) {
  border-top: none;
  vertical-align: baseline !important;
}

:deep(.quote-name) {
  font-size: 1.3rem;
  font-weight: bold;
}

:deep(.quote-body) {
  .table {
    margin-bottom: 0;
    border-radius: 4px 4px 0 0;
  }
}

.financing-option {
  display: flex;
  align-items: center;
  padding-left: 12px;
  border-top: 1px solid;
  background: #f4f5f8;
}

.overlay {
  position: absolute;
  background: rgba(255, 255, 255, 0.9);
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  color: var(--sera-color-text-body);
}
:deep(.header-link) {
  white-space: nowrap;
}
.divider {
  width: 1px;
  margin: 0 12px;
  height: 30px;
  background: var(--sera-color-neutral-gray-med-gray-500);
}
</style>
